/**
 * Pure TEA update for the nested custom-relation dialog.
 *
 * Shell: `EntityRelationshipDialog.vue`.
 */
import { assertNever } from "../lib/assertNever";
import type { UpdateFn } from "../lib/tea";

export type CustomRelationDialogModel = {
  readonly open: boolean;
  readonly draft: string;
  readonly inverseDraft: string;
};

export const initialCustomRelationDialogModel: CustomRelationDialogModel = {
  open: false,
  draft: "",
  inverseDraft: "",
};

export type CustomRelationDialogMsg =
  | { readonly type: "Open"; readonly draft: string }
  | { readonly type: "DraftChanged"; readonly draft: string }
  | { readonly type: "InverseChanged"; readonly inverseDraft: string }
  | { readonly type: "Commit" }
  | { readonly type: "Cancel" }
  | { readonly type: "CommitSucceeded" };

export type CustomRelationDialogCmd = {
  readonly kind: "addPair";
  readonly draft: string;
  readonly inverseDraft: string;
};

export function updateCustomRelationDialog(
  model: CustomRelationDialogModel,
  msg: CustomRelationDialogMsg,
): readonly [CustomRelationDialogModel, readonly CustomRelationDialogCmd[]] {
  switch (msg.type) {
    case "Open":
      return [{ open: true, draft: msg.draft, inverseDraft: "" }, []];
    case "DraftChanged":
      return [{ ...model, draft: msg.draft }, []];
    case "InverseChanged":
      return [{ ...model, inverseDraft: msg.inverseDraft }, []];
    case "Cancel":
      return [initialCustomRelationDialogModel, []];
    case "Commit": {
      if (model.draft.trim().length === 0) return [model, []];
      return [
        model,
        [
          {
            kind: "addPair",
            draft: model.draft,
            inverseDraft: model.inverseDraft,
          },
        ],
      ];
    }
    case "CommitSucceeded":
      return [initialCustomRelationDialogModel, []];
    default:
      return assertNever(msg);
  }
}

export const updateCustomRelationDialogFn: UpdateFn<
  CustomRelationDialogModel,
  CustomRelationDialogMsg,
  CustomRelationDialogCmd
> = updateCustomRelationDialog;
